import { CartLinesUpdateEvent } from '@shopify/events';

// Reserved-cart countdown for the cart drawer bar. A new window starts whenever
// the cart contents change; when it runs out the bar hides instead of restarting.
const KEY = 'fs-cart-reserve';
let memo = {};

function readState() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '{}');
  } catch {
    return memo;
  }
}

function writeState(state) {
  memo = state;
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* storage blocked: the in-memory copy still drives this page */
  }
}

class FsCartTimer extends HTMLElement {
  static observedAttributes = ['data-sig'];

  connectedCallback() {
    this.#arm();
    this.#tick();
    this.interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.#arm();
    this.#tick();
  }

  #arm() {
    const sig = this.dataset.sig || '';
    const minutes = Number(this.dataset.minutes) || 10;
    const state = readState();
    if (state.sig !== sig || !state.end) writeState({ sig, end: Date.now() + minutes * 60000 });
  }

  #tick() {
    const { end } = readState();
    const left = Math.max(0, Math.round(((end || 0) - Date.now()) / 1000));
    this.hidden = left === 0;
    const out = this.querySelector('[data-fs-time]');
    if (!out) return;
    const text = `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
    if (out.textContent !== text) out.textContent = text;
  }
}

if (!customElements.get('fs-cart-timer')) customElements.define('fs-cart-timer', FsCartTimer);


// ---- Upsell row: each card maps its option selects to a variant, then adds it.
// Listeners sit on the document because Horizon morphs the drawer's HTML on
// every cart change; anything bound to the old nodes would be lost.
function cardVariants(card) {
  try {
    return JSON.parse(card.querySelector('[data-fs-upsell-variants]')?.textContent || '[]');
  } catch {
    return [];
  }
}

function syncCard(card) {
  const button = card.querySelector('[data-fs-upsell-add]');
  if (!button) return;
  const picks = Array.from(card.querySelectorAll('[data-fs-upsell-opt]')).map((s) => s.value);
  const match = cardVariants(card).find((v) => picks.every((value, i) => v.o[i] === value));
  const ok = !!match && match.a;
  if (match) button.dataset.variantId = String(match.id);
  button.disabled = !ok;
  button.textContent = ok ? button.dataset.label : button.dataset.soldOut;
}

let adding = false;

async function addFromCard(button) {
  if (adding || button.disabled) return;
  const id = Number(button.dataset.variantId);
  if (!id) return;
  adding = true;
  button.setAttribute('aria-busy', 'true');

  // Horizon re-renders the drawer from section HTML returned by the add call,
  // so ask for the same sections its own product form asks for.
  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((node) => {
    if (node instanceof HTMLElement && node.dataset.sectionId) sectionIds.push(node.dataset.sectionId);
  });

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(id), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionIds.join(',') }),
    });
    const body = await res.json();
    if (!res.ok || body.status) throw new Error(body?.description || body?.message || 'Add to cart failed');
    const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`).then((r) => r.json());
    // The event contract wants a Storefront-shaped cart, not the AJAX body.
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'fs-cart-upsell',
        sourceId: 'fs-cart-upsell',
        itemCount: 1,
        sections: body.sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    button.textContent = button.dataset.soldOut;
  } finally {
    adding = false;
    button.removeAttribute('aria-busy');
  }
}

document.addEventListener('change', (event) => {
  const select = event.target instanceof Element ? event.target.closest('[data-fs-upsell-opt]') : null;
  const card = select?.closest('[data-fs-upsell]');
  if (card) syncCard(card);
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const pick = target.closest('[data-fs-pick-more]');
  if (pick) {
    event.preventDefault();
    const row = document.getElementById('fs-cart-upsell');
    row?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }

  const add = target.closest('[data-fs-upsell-add]');
  if (add) {
    event.preventDefault();
    addFromCard(add);
  }
});

// ---- Shipping countdown (snippets/fs-cart-ship-eta.liquid): counts down to the
// visitor's local midnight and names the ship day. Ticks every node each second,
// so a drawer that Horizon has just re-rendered picks the timer straight up.
const pad = (n) => String(n).padStart(2, '0');
const dayStart = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const nextShipDay = (orderTime, weekends) => {
  const d = dayStart(orderTime);
  d.setDate(d.getDate() + 1);
  if (!weekends) while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d;
};

function renderEta(el) {
  const msg = el.querySelector('[data-fs-eta-msg]');
  if (!msg) return;
  const now = new Date();
  const midnight = dayStart(now);
  midnight.setDate(midnight.getDate() + 1);
  const weekends = el.dataset.shipWeekends === 'true';
  const shipNow = nextShipDay(now, weekends);
  const shipLater = nextShipDay(midnight, weekends);
  const lang = document.documentElement.lang || 'en';
  const dayLabel =
    shipNow.getTime() === midnight.getTime()
      ? el.dataset.tomorrow || 'tomorrow'
      : (el.dataset.dayPrefix || '') + shipNow.toLocaleDateString(lang, { weekday: 'long' });
  const tpl = (shipNow.getTime() === shipLater.getTime() ? el.dataset.tplSame : el.dataset.tpl) || '';
  const ms = Math.max(0, midnight - now);
  const timer = `${Math.floor(ms / 3600000)}h ${pad(Math.floor((ms % 3600000) / 60000))}m ${pad(Math.floor((ms % 60000) / 1000))}s`;
  const frag = document.createDocumentFragment();
  tpl.split(/(\[timer\]|\[day\])/).forEach((part) => {
    if (part === '[timer]' || part === '[day]') {
      const b = document.createElement('span');
      b.className = part === '[timer]' ? 'fs-eta__timer' : 'fs-eta__day';
      b.textContent = part === '[timer]' ? timer : dayLabel;
      frag.appendChild(b);
    } else if (part) frag.appendChild(document.createTextNode(part));
  });
  msg.replaceChildren(frag);
}

// Country: the server prints Shopify's localization country. For a visitor outside
// every market that falls back to the primary market, so Shopify's own IP lookup
// (cached for the session) overrides it, with Shopify's flag image for the new code.
let geo = null;
try {
  geo = sessionStorage.getItem('fs-geo');
} catch {
  /* storage blocked */
}
if (!geo) {
  fetch('/browsing_context_suggestions.json?country[enabled]=true')
    .then((r) => r.json())
    .then((j) => {
      geo = j?.detected_values?.country?.handle || null;
      try {
        if (geo) sessionStorage.setItem('fs-geo', geo);
      } catch {
        /* storage blocked */
      }
    })
    .catch(() => {});
}

function fixCountry(el) {
  if (!geo || !/^[A-Z]{2}$/i.test(geo) || el.dataset.cc === geo) return;
  el.dataset.cc = geo;
  const code = el.querySelector('[data-fs-eta-cc]');
  if (code) code.textContent = geo;
  const img = el.querySelector('[data-fs-eta-flag], .fs-eta__flag');
  if (img) {
    img.removeAttribute('srcset');
    img.src = `https://cdn.shopify.com/static/images/flags/${geo.toLowerCase()}.svg`;
    img.alt = geo;
  }
}

function tickEta() {
  document.querySelectorAll('[data-fs-eta]').forEach((el) => {
    renderEta(el);
    fixCountry(el);
  });
}
tickEta();
setInterval(tickEta, 1000);
